import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useAuth } from "@/components/AuthProvider";
import { useDashboardRouter } from "@/hooks/useDashboardRouter";
import { invalidateConnectedGithubOrgs, useConnectedGithubOrgsNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { replaceOrgSegment, resolveUrlOrgAccess } from "@/lib/urlOrgAccess";
export type UrlOrgAccessGate = {
  state: "allowed";
} | {
  state: "pending";
} | {
  state: "denied";
  org: string;
  fallbackOrg: FallbackOrg | null;
};
export interface FallbackOrg {
  login: string;
  instanceSlug: string;
}

/**
 * Gates `/<org>/...` pages on the URL org being one of the user's. Before
 * denying, it fixes a case-only mismatch and refetches the org list live once
 * per org, since the cached list misses orgs the user just joined.
 */
export function useUrlOrgAccessGate() {
  const $ = _c(30);
  const router = useDashboardRouter();
  const {
    user
  } = useAuth();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  const queryClient = useQueryClient();
  const refreshStartedFor = useRef(null);
  const [refreshedOrg, setRefreshedOrg] = useState(null);
  const urlOrg = typeof router.query.org === "string" ? router.query.org : undefined;
  let t0;
  if ($[0] !== router.pathname) {
    t0 = router.pathname.startsWith("/admin/internal-monitors");
    $[0] = router.pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const exempt = t0;
  let t1;
  if ($[2] !== connectedGithubOrgs || $[3] !== exempt || $[4] !== urlOrg || $[5] !== user) {
    const logins = connectedGithubOrgs?.installations.map(_temp) ?? [];
    t1 = urlOrg && !exempt && user && connectedGithubOrgs ? resolveUrlOrgAccess(urlOrg, logins, user.is_superuser) : null;
    $[2] = connectedGithubOrgs;
    $[3] = exempt;
    $[4] = urlOrg;
    $[5] = user;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const access = t1;
  const canonicalLogin = access?.kind === "wrongCase" ? access.login : null;
  const isMissing = access?.kind === "missing";
  const orgsInstanceSlug = connectedGithubOrgs?.instance_slug;
  let t2;
  let t3;
  if ($[7] !== canonicalLogin || $[8] !== router || $[9] !== urlOrg) {
    t2 = () => {
      if (!canonicalLogin || !urlOrg) {
        return;
      }
      const asPath = router.asPath.includes("#") ? router.asPath : `${router.asPath}${window.location.hash}`;
      router.replace(replaceOrgSegment(asPath, urlOrg, canonicalLogin));
    };
    t3 = [canonicalLogin, urlOrg, router];
    $[7] = canonicalLogin;
    $[8] = router;
    $[9] = urlOrg;
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[12] !== isMissing || $[13] !== orgsInstanceSlug || $[14] !== queryClient || $[15] !== urlOrg) {
    t4 = () => {
      if (!isMissing || !urlOrg || refreshStartedFor.current === urlOrg) {
        return;
      }
      refreshStartedFor.current = urlOrg;
      invalidateConnectedGithubOrgs(queryClient, orgsInstanceSlug).finally(() => {
        if (refreshStartedFor.current === urlOrg) {
          setRefreshedOrg(urlOrg);
        }
      });
    };
    t5 = [isMissing, urlOrg, orgsInstanceSlug, queryClient];
    $[12] = isMissing;
    $[13] = orgsInstanceSlug;
    $[14] = queryClient;
    $[15] = urlOrg;
    $[16] = t4;
    $[17] = t5;
  } else {
    t4 = $[16];
    t5 = $[17];
  }
  useEffect(t4, t5);
  if (!access || access.kind === "allowed" || !urlOrg) {
    let t6;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        state: "allowed"
      };
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    return t6;
  }
  if (access.kind === "wrongCase" || refreshedOrg !== urlOrg) {
    let t6;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        state: "pending"
      };
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    return t6;
  }
  let t6;
  if ($[20] !== connectedGithubOrgs?.installations) {
    t6 = connectedGithubOrgs?.installations ?? [];
    $[20] = connectedGithubOrgs?.installations;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const installations = t6;
  let t7;
  if ($[22] !== connectedGithubOrgs?.active_org_name || $[23] !== installations) {
    t7 = installations.find(installation_0 => installation_0.account.login === connectedGithubOrgs?.active_org_name) ?? installations[0];
    $[22] = connectedGithubOrgs?.active_org_name;
    $[23] = installations;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const fallback = t7;
  let t8;
  if ($[25] !== fallback) {
    t8 = fallback ? {
      login: fallback.account.login,
      instanceSlug: fallback.instance_slug
    } : null;
    $[25] = fallback;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t8 || $[28] !== urlOrg) {
    t9 = {
      state: "denied",
      org: urlOrg,
      fallbackOrg: t8
    };
    $[27] = t8;
    $[28] = urlOrg;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
function _temp(installation) {
  return installation.account.login;
}